
import React, { useContext, useState } from "react"
import { CustomSwitch, Divider, ReadOnlyBadge } from "@components"
import { NotificationContext, UserContext } from "@context"
import { hasExpired, mySqlToLocal } from "@helpers"
import { useUpdateEvent } from "@hooks"
import { useApolloClient } from "@apollo/client";
import { Button, Chip, Slider } from "@heroui/react"

import PropTypes from "prop-types"

const EventSettings = ({ backButton, event }) => {
  const initialFormData = {
    vcAutoDisconnect: event.vc_auto_disconnect,
    maxBookingsPerStudent: event.max_bookings_per_student,
  }

  /**
   * State
   */
  const [form, setForm] = useState({ ...initialFormData });
  const [savedFormData, setSavedFormData] = useState({ ...initialFormData });

  /**
   * Context
   */
  const { setCurrentEvent } = useContext(UserContext)
  const { notify } = useContext(NotificationContext)

  /**
   * Hooks
   */
  const client = useApolloClient();
  const {updateEvent, loading} = useUpdateEvent()

  /**
   * Functions
   */
  const handleSave = () => {
    updateEvent({ 
      variables: {
        input: {
          id: event.id,
          vc_auto_disconnect: form.vcAutoDisconnect,
          max_bookings_per_student: form.maxBookingsPerStudent,
        }
      },
      
      onCompleted: (response) => {
        notify({ title: "Success", type: "success", content: `Event updated successfully.`, open: true })
        setCurrentEvent(event.id) // forces user context to refresh event data
        client.refetchQueries({ include: "active" }); // refetch all active queries to reflect the new session

        setSavedFormData({ ...form })
      },

      onError: (error) => {
        notify({ title: "Error", type: "danger", content: `An error occurred while updating the event.`, open: true })
      }
    })
  }

  const currentMaxBookings = form.maxBookingsPerStudent === 0 ? "Unlimited" : `${form.maxBookingsPerStudent} Bookings`
  const isDirty = JSON.stringify(form) !== JSON.stringify(savedFormData)
  const isReadOnlyMode = hasExpired(mySqlToLocal(event.end_date))
  const isVideoConferenceEvent = event.meetingTypes.some(meetingType => meetingType.type === "VC")

  return (
    <div>
      <div className="flex justify-end">
        {isReadOnlyMode && <ReadOnlyBadge className="mb-4" isFullWidth size="lg" />}
      </div>

      <div className="mb-6">
        {isVideoConferenceEvent && (
          <React.Fragment>
            <CustomSwitch
              title="VC Auto Disconnect"
              description="Automatically disconnect video calls when the slot end time has been reached."
              isSelected={form.vcAutoDisconnect}
              handleChange={(isSelected) => setForm(prevForm => ({...prevForm, vcAutoDisconnect: isSelected}))}
              isDisabled={isReadOnlyMode}
            />
            
            <Divider className="my-3" />
          </React.Fragment>
        )}

        <div className="flex items-center justify-between">
          <p className="font-semibold">Max Bookings Per Student</p>
          <Chip className="text-white" color="success">
            <span className="font-semibold">{currentMaxBookings}</span>
          </Chip>
        </div>
        
        <p className="mb-3 text-content2-foreground">Set the maximum number of bookings a guardian can make per student for this event. If 0 then no limit is set.</p>
        
        <Slider
          aria-label="Max Bookings Per Student"
          classNames={{ thumb: "shadow-none bg-blue-600 after:bg-blue-600 after:shadow-none h-6 w-6 border-none" }}
          value={form.maxBookingsPerStudent}
          onChange={(value) => setForm(prevForm => ({...prevForm, maxBookingsPerStudent: value}))}
          maxValue={100}
          minValue={0}
          size="md"
          step={1}
          isDisabled={isReadOnlyMode}
        />
      </div>
      
      <Button className="font-semibold" color="secondary" isDisabled={!isDirty} fullWidth onPress={() => handleSave()} isLoading={loading}>Save</Button>
      {backButton}
    </div>
  )
}

EventSettings.propTypes = {
  backButton: PropTypes.element.isRequired,
  event: PropTypes.shape({
    id: PropTypes.string.isRequired,
    vc_auto_disconnect: PropTypes.bool.isRequired,
    max_bookings_per_student: PropTypes.number.isRequired,
    meetingTypes: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string.isRequired,
      type: PropTypes.string.isRequired,
    })).isRequired,
  }).isRequired,
}

export default EventSettings